import {
  compareAsc as compareAscFn,
  compareDesc as compareDescFn,
  differenceInHours as differenceInHoursFn,
  differenceInMinutes as differenceInMinutesFn,
  differenceInWeeks as differenceInWeeksFn,
  differenceInDays as differenceInDaysFn,
  type Duration,
  format as formatFn,
  formatDuration as formatDurationFn,
  formatISO as formatISOFn,
  intervalToDuration,
  isEqual as isEqualFn,
  isFuture as isFutureFn,
  isPast as isPastFn,
  isThisWeek as isThisWeekFn,
  isToday as isTodayFn,
  isWithinInterval as isWithinIntervalFn,
  parseISO as parseISOFn,
  formatDistanceToNow as formatDistanceToNowFn,
} from 'date-fns'
import { utcToZonedTime as utcToZonedTimeFn } from 'date-fns-tz'

export { parse } from 'date-fns'

/**
 * Compare the two dates and return 1 if the first date is after the second, -1
 * if the first date is before the second or 0 if dates are equal.
 */
export function compareAsc(dateLeft: Date | number, dateRight: Date | number): number {
  return compareAscFn(dateLeft, dateRight)
}

/**
 * Compare the two dates and return -1 if the first date is after the second, 1
 * if the first date is before the second or 0 if dates are equal.
 */
export function compareDesc(dateLeft: Date | number, dateRight: Date | number): number {
  return compareDescFn(dateLeft, dateRight)
}

/**
 * Transforms a string or null into a Date or Undefined
 */
export function createDate(date?: string | null): Date | undefined {
  return date ? new Date(date) : undefined
}

/**
 * Get the difference in hours between 2 dates
 */
export function differenceInHours(dateLeft: Date, dateRight: Date) {
  return differenceInHoursFn(dateLeft, dateRight)
}

/**
 * Get the absolute difference in minutes between 2 dates
 */
export function differenceInMinutes(dateLeft: Date, dateRight: Date) {
  return Math.abs(differenceInMinutesFn(dateLeft, dateRight))
}

/**
 * Get the number of full weeks between two dates. The dates order doesn't matter.
 */
export function differenceInWeeks(dateLeft: Date | number, dateRight: Date | number): number {
  // The order matters on date-fns, if the dateLeft is lower than the dateRight,
  // it will return a negative number, even negative zero (-0) is returned.
  // This turns the API usage really confusing, so a Math.abs is being applied
  return Math.abs(differenceInWeeksFn(dateLeft, dateRight))
}

/**
 * Get the number of full days between two dates. The dates order doesn't matter.
 */
export function differenceInDays(dateLeft: Date | number, dateRight: Date | number): number {
  return Math.abs(differenceInDaysFn(dateLeft, dateRight))
}

/** Get the distance between the given date and now in words. */
export function formatDistanceToNow(
  date: Date,
  options?: { addSuffix?: boolean; locale?: Locale }
) {
  return formatDistanceToNowFn(date, options)
}

/**
 * Format date
 */
export function formatDate(date: Date, format: string, timeZone?: string): string {
  if (timeZone) {
    const zonedDate = utcToZonedTimeFn(date, timeZone)
    return formatFn(zonedDate, format)
  }

  return formatFn(date, format)
}

/**
 * It formats the difference between 2 dates to something like: 8 days 4 hours 33 minutes
 */
export function formatDuration({
  start,
  end,
  format = ['years', 'months', 'days', 'hours', 'minutes'],
  delimiter = ' ',
}: {
  start: Date
  end: Date
  format?: Array<string>
  delimiter?: string
}) {
  const duration = intervalToDuration({
    start,
    end,
  })

  return formatDurationFn(duration, {
    format,
    delimiter,
  })
}

/**
 * It formats a Date to ISO String
 */
export function formatISO(date: Date): string {
  return formatISOFn(date)
}

/**
 * Determine if a provided value is a Date or not
 */
export function isDate(value?: unknown): value is Date {
  return value instanceof Date
}

/**
 * Determine if the provided date do not have a time part
 */
export function isDateOnly(date: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date)
}

/**
 * Are the given dates equal?
 */
export function isEqual(dateLeft: Date | number, dateRight: Date | number): boolean {
  return isEqualFn(dateLeft, dateRight)
}

/**
 * Is the given date in the future?
 */
export function isFuture(date: Date | number): boolean {
  return isFutureFn(date)
}

/**
 * Is the given date in the past?
 */
export function isPast(date: Date | number): boolean {
  return isPastFn(date)
}

/**
 * Is the given date in the same week as the current date?
 */
export function isThisWeek(date: Date | number): boolean {
  return isThisWeekFn(date)
}

/**
 * Is the given date today?
 */
export function isToday(date: Date | number): boolean {
  return isTodayFn(date)
}

/**
 * It formats a String ISO to Date
 */
export function parseISO(date: string): Date {
  return parseISOFn(date)
}

/**
 * Is the given duration valid (not undefined or zero)?
 */
export function isValidDuration(duration: Duration, field: keyof Duration) {
  if (duration[field] === undefined || duration[field] === 0) return false
  return true
}

/**
 * Is the given date within the interval? (Including start and end)
 */
export function isWithinInterval(date: Date, { start, end }: { start: Date; end: Date }): boolean {
  return isWithinIntervalFn(date, { start, end })
}

/**
 * Format a UTC datetime to the browser's local timezone.
 */
export function formatToLocaleDate(
  datetime: Date | null | undefined,
  formatStr: string = 'EEE, MMM d'
): string | null {
  if (!datetime) return null

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDate = utcToZonedTimeFn(datetime, userTimezone)
  return formatFn(zonedDate, formatStr)
}

export const formats = {
  shortdate: 'EEE, MMM dd',
  twentyfour: 'HH:mm',
}
